/**
 * GenerationLifecycleService — module-level owner of generation run state.
 *
 * Phase 1 of the virtualization-hazards plan. ReactFlow's
 * onlyRenderVisibleElements unmounts node components that scroll
 * off-screen, which killed or split-brained every piece of run state that
 * lived inside components: batch cancel refs, batch output-node refs,
 * timer start times, and flow attribution read at completion time. This
 * service generalizes the module-level-Map fix from the LoadingOverlay
 * timer (d93737f) into a single registry that owns the whole run:
 *
 *   - start(nodeId)   → stamps startTime, captures flowId, clears cancel
 *   - requestCancel() → store-backed, works regardless of which component
 *                       instance started the run
 *   - deliverMediaOutput() → the completion tail (output-node spawn /
 *                       page append + gallery refresh event), executed via
 *                       FlowStoreRef so it works with zero mounted
 *                       components, and guarded against writing into the
 *                       wrong flow after a mid-run flow switch
 *   - finish(nodeId)  → clears the registry entry
 *
 * Phase 1 ported the image handler family; Phase 2 generalized the same
 * delivery tail to the video/text families via deliverMediaOutput.
 */

import type { Node, Edge } from '@xyflow/react';
import { getFlowStore } from './FlowStoreRef';
import { getOutputNodeDimensions } from '../config/nodeRegistry';
import { supabaseFlowStorageService as flowStorageService } from './SupabaseFlowStorageService';
import { toastService } from '../components/ui/Toast';
import logger from '../utils/logger';

export interface GenerationRun {
  nodeId: string;
  /** Flow the run belongs to, captured at start — NOT at completion. */
  flowId?: string;
  /** Epoch ms when the run started. Read by LoadingOverlay across remounts. */
  startTime: number;
  cancelRequested: boolean;
  /**
   * In 'single' batch-output mode, the output node that batch pages append
   * to. Owned here (not in a component ref) so a scroll-driven remount
   * mid-batch can't fork a second output node.
   */
  batchOutputNodeId: string | null;
}

class GenerationLifecycleService {
  private runs = new Map<string, GenerationRun>();

  /**
   * Begin a run for a node. Idempotent per node: starting again replaces
   * the previous entry (handlers already guard concurrent runs via
   * data.isProcessing).
   */
  start(nodeId: string): GenerationRun {
    const run: GenerationRun = {
      nodeId,
      flowId: flowStorageService.getCurrentFlowId() || undefined,
      startTime: Date.now(),
      cancelRequested: false,
      batchOutputNodeId: null,
    };
    this.runs.set(nodeId, run);
    return run;
  }

  /** Clear the run entry. Call from the handler's finally block. */
  finish(nodeId: string): void {
    this.runs.delete(nodeId);
  }

  getRun(nodeId: string): GenerationRun | undefined {
    return this.runs.get(nodeId);
  }

  /** Start time for the node's active run, if any (LoadingOverlay). */
  getStartTime(nodeId: string): number | undefined {
    return this.runs.get(nodeId)?.startTime;
  }

  /**
   * Request cancellation. Store-backed: works no matter which component
   * instance (or none) is currently mounted for the node.
   */
  requestCancel(nodeId: string): void {
    const run = this.runs.get(nodeId);
    if (run) run.cancelRequested = true;
  }

  isCancelRequested(nodeId: string): boolean {
    return this.runs.get(nodeId)?.cancelRequested ?? false;
  }

  clearCancel(nodeId: string): void {
    const run = this.runs.get(nodeId);
    if (run) run.cancelRequested = false;
  }

  /**
   * Completion tail for image AND video generations: append the media to the
   * connected output node ('single' mode) or spawn a new output node in a
   * fan-block grid ('multiple' mode / no output connected). Runs entirely
   * against the canvas store via FlowStoreRef — no mounted node component
   * required.
   *
   * Ported verbatim from NodeAdapters.addImageToOutputs; behavior changes:
   *   - batchOutputNodeId lives in the run registry, not a component ref.
   *   - If the user switched flows mid-run, we REFUSE to write nodes into
   *     the now-open (wrong) flow. The asset itself is safe — it was
   *     uploaded flow-scoped with the flowId captured at start.
   *
   * `mediaType` parameterizes the ONLY media-specific bits (page/node/edge
   * media type, edge stroke colour, output-node dimensions). Everything else
   * — run-scoped target, fan-grid re-layout, guards, id suffixing — is shared
   * so image and video delivery stay in lockstep.
   */
  async deliverMediaOutput(
    nodeId: string,
    url: string,
    mediaType: 'image' | 'video' | 'audio',
    outputMode: 'single' | 'multiple',
    batchIndex: number,
  ): Promise<void> {
    const run = this.runs.get(nodeId);
    const store = getFlowStore();

    const currentFlowId = flowStorageService.getCurrentFlowId() || undefined;
    if (run?.flowId && currentFlowId && run.flowId !== currentFlowId) {
      logger.warn('[GenerationLifecycle] Flow switched mid-generation; not writing output into the open flow', {
        nodeId, runFlowId: run.flowId, currentFlowId,
      });
      // Media-neutral copy so a video run doesn't say "image".
      toastService.info(
        'A generation from another Knod finished — the result was saved to that Knod\'s assets.'
      );
      window.dispatchEvent(new CustomEvent('fileStorageUpdated'));
      return;
    }

    if (!store) {
      // Canvas unmounted entirely (e.g. user is on the dashboard). The
      // asset is already uploaded flow-scoped; just refresh any listeners.
      logger.warn('[GenerationLifecycle] No canvas mounted; skipping output-node delivery', { nodeId });
      window.dispatchEvent(new CustomEvent('fileStorageUpdated'));
      return;
    }

    const { setNodes, setEdges, getNode, getNodes, getEdges } = store;
    // Edge stroke by media type — kept in sync with EDGE_COLOR_MAP (nodeConfig.ts):
    // audio = green #10B981 (the canonical audio connector-type color), video = red,
    // image = cyan.
    const edgeStroke = mediaType === 'video' ? '#FF4B42' : mediaType === 'audio' ? '#10B981' : '#42C3D7';

    const outgoingEdges = getEdges().filter(edge => edge.source === nodeId);
    const connectedOutputNode = outgoingEdges.find(edge => {
      const targetNode = getNode(edge.target);
      return targetNode?.type === 'universalOutput' || targetNode?.type === 'customOutput';
    });

    if (outputMode === 'single') {
      const targetNodeId = run?.batchOutputNodeId || connectedOutputNode?.target || null;
      if (targetNodeId) {
        if (run) run.batchOutputNodeId = targetNodeId;
        setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === targetNodeId) {
              const existingPages = Array.isArray(node.data.pages) ? node.data.pages : [];
              const newPage = {
                content: url,
                context: null,
                type: mediaType,
                timestamp: new Date().toISOString()
              };
              const newPageIndex = existingPages.length;
              return {
                ...node,
                data: {
                  ...node.data,
                  content: url,
                  type: mediaType,
                  outputsMediaType: mediaType,
                  output: url,
                  pages: [...existingPages, newPage],
                  currentPageIndex: newPageIndex
                }
              };
            }
            return node;
          })
        );
        window.dispatchEvent(new CustomEvent('fileStorageUpdated'));
        return;
      }
    }

    const sourceNode = getNode(nodeId);
    if (!sourceNode) {
      return;
    }
    const sourceWidth = sourceNode.measured?.width || sourceNode.width || 320;
    const sourceHeight = sourceNode.measured?.height || sourceNode.height || 200;
    const outputNodeId = `customOutput-${Date.now()}-${batchIndex}`;
    const initialPage = {
      content: url,
      context: null,
      type: mediaType,
      timestamp: new Date().toISOString()
    };

    const outputDims = getOutputNodeDimensions(mediaType);
    const outputNode: Node = {
      id: outputNodeId,
      type: 'universalOutput',
      position: { x: 0, y: 0 }, // placeholder — overwritten by grid re-layout below
      width: outputDims.width,
      height: outputDims.height,
      data: {
        output: url,
        content: url,
        type: mediaType,
        outputsMediaType: mediaType,
        pages: [initialPage],
        currentPageIndex: 0
      }
    };

    const edge: Edge = {
      id: `${nodeId}-${outputNodeId}`,
      source: nodeId,
      target: outputNodeId,
      type: mediaType,
      animated: true,
      style: { stroke: edgeStroke, strokeWidth: 3 }
    };

    if (outputMode === 'single' && run) {
      run.batchOutputNodeId = outputNodeId;
    }

    // Re-layout ALL output children of this parent (existing + the new one)
    // using the same fan-block grid rules as handleAutoArrange so subsequent
    // generations don't overlap prior results. Column-major grid, centered
    // vertically on the parent; spacing matches auto-arrange constants so a
    // later Auto Arrange is a no-op for this cluster.
    const CHILD_WIDTH = outputDims.width;
    const CHILD_HEIGHT = outputDims.height;
    const GAP_X = 100;
    const GAP_Y = 40;
    const SUB_COL_GAP = GAP_X * 0.5;
    const FAN_MIN = 6;
    const FAN_3COL = 11;
    const isOutputType = (t?: string) => t === 'universalOutput' || t === 'customOutput';

    const existingChildIds = getEdges()
      .filter((e) => e.source === nodeId)
      .map((e) => e.target)
      .filter((tid) => {
        const n = getNode(tid);
        return n ? isOutputType(n.type) : false;
      });
    const allChildIds: string[] = Array.from(new Set([...existingChildIds, outputNodeId]));
    const total = allChildIds.length;
    const cols = total < FAN_MIN ? 1 : total >= FAN_3COL ? 3 : 2;
    const rows = Math.max(1, Math.ceil(total / cols));
    const blockWidth = cols * CHILD_WIDTH + (cols - 1) * SUB_COL_GAP;
    const blockHeight = rows * CHILD_HEIGHT + (rows - 1) * GAP_Y;
    const parentCenterY = sourceNode.position.y + sourceHeight / 2;
    const initialLeftX = sourceNode.position.x + sourceWidth + GAP_X;
    const gridTopY = parentCenterY - blockHeight / 2;

    // Shift the whole cluster right if it would collide with any unrelated
    // node on the canvas (not the parent, not one of its own children). On
    // collision we jump past the rightmost collider + GAP_X, then re-check —
    // a new shifted position may overlap something else. Capped at 8 passes.
    const excludeIds = new Set<string>([nodeId, ...allChildIds]);
    const nodesSnapshot = getNodes();
    const boundsOf = (n: Node) => ({
      left: n.position.x,
      right: n.position.x + (n.measured?.width || n.width || 280),
      top: n.position.y,
      bottom: n.position.y + (n.measured?.height || n.height || 200),
    });
    let gridLeftX = initialLeftX;
    for (let pass = 0; pass < 8; pass += 1) {
      const clusterLeft = gridLeftX;
      const clusterRight = gridLeftX + blockWidth;
      const clusterTop = gridTopY;
      const clusterBottom = gridTopY + blockHeight;
      let maxColliderRight = -Infinity;
      for (const n of nodesSnapshot) {
        if (excludeIds.has(n.id)) continue;
        const b = boundsOf(n);
        const overlaps =
          b.left < clusterRight && b.right > clusterLeft &&
          b.top < clusterBottom && b.bottom > clusterTop;
        if (overlaps && b.right > maxColliderRight) maxColliderRight = b.right;
      }
      if (maxColliderRight === -Infinity) break; // no collision, done
      gridLeftX = maxColliderRight + GAP_X;
    }

    const positionFor = (idx: number) => ({
      x: gridLeftX + Math.floor(idx / rows) * (CHILD_WIDTH + SUB_COL_GAP),
      y: gridTopY + (idx % rows) * (CHILD_HEIGHT + GAP_Y),
    });
    const childIdxById = new Map(allChildIds.map((cid, i) => [cid, i]));

    setNodes((nodes) => {
      const withNew = [...nodes, outputNode];
      return withNew.map((n) => {
        const idx = childIdxById.get(n.id);
        return idx === undefined ? n : { ...n, position: positionFor(idx) };
      });
    });
    setEdges((edges) => [...edges, edge]);
    window.dispatchEvent(new CustomEvent('fileStorageUpdated'));
  }

  /**
   * Thin image wrapper — preserves the original image-only entry point so
   * existing image callers get ZERO behavior change.
   */
  async deliverImageOutput(
    nodeId: string,
    imageUrl: string,
    outputMode: 'single' | 'multiple',
    batchIndex: number,
  ): Promise<void> {
    return this.deliverMediaOutput(nodeId, imageUrl, 'image', outputMode, batchIndex);
  }

  /** Thin video wrapper — video counterpart of deliverImageOutput. */
  async deliverVideoOutput(
    nodeId: string,
    videoUrl: string,
    outputMode: 'single' | 'multiple',
    batchIndex: number,
  ): Promise<void> {
    return this.deliverMediaOutput(nodeId, videoUrl, 'video', outputMode, batchIndex);
  }

  /** Thin audio wrapper — audio counterpart of deliverImageOutput (ElevenLabs nodes). */
  async deliverAudioOutput(
    nodeId: string,
    audioUrl: string,
    outputMode: 'single' | 'multiple',
    batchIndex: number,
  ): Promise<void> {
    return this.deliverMediaOutput(nodeId, audioUrl, 'audio', outputMode, batchIndex);
  }
}

export const generationLifecycleService = new GenerationLifecycleService();
export default generationLifecycleService;
